import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Place as PlaceIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import Layout from "../components/Layout";
import { useVenues } from "../contexts/VenueContext";
import { getMapsUrl } from "../utils/venue";

interface VenueItem {
  id: string;
  name: string;
  address: string;
}

const emptyForm = { name: "", address: "" };

const VenueSettings: React.FC = () => {
  const { venues, isLoading: loading, refreshVenues } = useVenues();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<VenueItem | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<VenueItem | null>(null);

  const openAdd = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (item: VenueItem) => {
    setEditingItem(item);
    setForm({ name: item.name, address: item.address });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.address.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editingItem) {
        await apiRequest(`/api/venues/${editingItem.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        setSuccess("Venue updated.");
      } else {
        await apiRequest("/api/venues", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        setSuccess("Venue added.");
      }
      await refreshVenues();
      closeDialog();
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError(null);
    try {
      await apiRequest(`/api/venues/${deleteTarget.id}`, { method: "DELETE" });
      await refreshVenues();
      setSuccess("Venue deleted.");
      setDeleteTarget(null);
    } catch {
      setError("Failed to delete. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 800, mx: "auto", p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <PlaceIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" fontWeight={800}>
                Venues
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage the club locations league days can be assigned to.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Add Venue
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && venues.length === 0 && (
          <Paper
            variant="outlined"
            sx={{ p: 6, textAlign: "center", borderStyle: "dashed", borderRadius: 3 }}
          >
            <PlaceIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No venues yet
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              Add each club location so it can be assigned to league days and shown clearly to players.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Add First Venue
            </Button>
          </Paper>
        )}

        {!loading && venues.length > 0 && (
          <Box>
            {venues.map((venue: any) => (
              <Card
                key={venue.id}
                variant="outlined"
                sx={{ mb: 1.5, borderRadius: 2, transition: "box-shadow 0.15s", "&:hover": { boxShadow: 2 } }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {venue.name}
                    </Typography>
                    <Typography
                      component="a"
                      href={getMapsUrl(venue.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      color="text.secondary"
                      sx={{ textDecoration: "none", display: "block", mt: 0.25 }}
                    >
                      📍 {venue.address}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(venue)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteTarget(venue)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {editingItem ? "Edit Venue" : "Add Venue"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField
            label="Name"
            fullWidth
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Court Club Stuttgart"
          />
          <TextField
            label="Address"
            fullWidth
            multiline
            minRows={2}
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            placeholder="Street, postal code, city"
            helperText="Used to generate the tap-to-navigate map link shown to players"
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.address.trim()}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 90 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : editingItem ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>Delete venue?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently remove:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            "{deleteTarget?.name}"
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
            League days that already reference this venue keep showing its name and address as they were.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 90 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default VenueSettings;
